import React, { useState, useRef } from 'react';
import { Plus, Trash, ArrowLeft, Pencil, Play, FileText, FilePdf, X, ArrowUp, ArrowDown, UploadSimple, Link as LinkIcon, CloudArrowUp } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import api from '../../lib/api';
import { toast } from 'sonner';

const getTierClassName = (tier) => {
  if (tier === 'elite') return 'bg-[#D4AF37]/20 text-[#D4AF37]';
  if (tier === 'premium') return 'bg-white/10 text-white';
  return 'bg-zinc-800 text-zinc-400';
};

const VideoInput = ({ videoUrl, videoSource, onChange, moduleIdx }) => {
  const [mode, setMode] = useState(videoSource === 'upload' ? 'upload' : 'url');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 500 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 500MB');
      return;
    }

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/admin/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(response.data.url, 'upload');
      toast.success('Video uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveVideo = async () => {
    if (videoSource === 'upload' && videoUrl) {
      const filename = videoUrl.split('/').pop();
      try {
        await api.delete(`/admin/upload/video/${filename}`);
      } catch {
        // File may already be deleted
      }
    }
    onChange('', '');
  };

  return (
    <div data-testid={`video-input-${moduleIdx}`}>
      <Label className="text-zinc-400 flex items-center gap-1 mb-2">
        <Play className="w-3 h-3" /> Video
      </Label>

      {/* Mode Toggle */}
      <div className="flex gap-1 mb-3">
        <Button
          type="button" size="sm"
          onClick={() => setMode('upload')}
          className={mode === 'upload' ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}
        >
          <UploadSimple className="w-3 h-3 mr-1" /> Upload
        </Button>
        <Button
          type="button" size="sm"
          onClick={() => setMode('url')}
          className={mode === 'url' ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'}
        >
          <LinkIcon className="w-3 h-3 mr-1" /> Paste URL
        </Button>
      </div>

      {mode === 'upload' ? (
        <div>
          {videoUrl && videoSource === 'upload' ? (
            <div className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-700 rounded">
              <Play className="w-5 h-5 text-green-500 flex-shrink-0" weight="fill" />
              <span className="text-sm text-zinc-300 flex-1 truncate">{videoUrl.split('/').pop()}</span>
              <Button type="button" variant="ghost" size="sm" onClick={handleRemoveVideo} className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${uploading ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5' : 'border-zinc-700 hover:border-[#D4AF37]/30'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg,.mp4,.webm,.mov,.avi"
                onChange={handleUpload}
                className="hidden"
                data-testid={`video-upload-${moduleIdx}`}
              />
              {uploading ? (
                <div>
                  <CloudArrowUp className="w-8 h-8 text-[#D4AF37] mx-auto mb-2 animate-pulse" weight="duotone" />
                  <p className="text-sm text-zinc-400 mb-2">Uploading... {progress}%</p>
                  <div className="w-full bg-zinc-800 h-2 rounded overflow-hidden max-w-xs mx-auto">
                    <div className="bg-[#D4AF37] h-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              ) : (
                <div>
                  <UploadSimple className="w-8 h-8 text-zinc-500 mx-auto mb-2" weight="duotone" />
                  <p className="text-sm text-zinc-400">Click to upload video</p>
                  <p className="text-xs text-zinc-600 mt-1">MP4, WebM, MOV, AVI — up to 500MB</p>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Input
          value={videoSource === 'url' ? (videoUrl || '') : ''}
          onChange={(e) => onChange(e.target.value, 'url')}
          className="bg-zinc-900 border-zinc-700 text-white"
          placeholder="https://www.youtube.com/embed/... or any video URL"
          data-testid={`module-video-${moduleIdx}`}
        />
      )}
    </div>
  );
};

const emptyModule = () => ({
  id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title: '',
  video_url: '',
  duration: '',
  text_content: '',
  pdf_urls: [],
});

const ModuleEditor = ({ course, onBack, onRefresh }) => {
  const [modules, setModules] = useState(course.modules || []);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const addModule = () => {
    const newMod = emptyModule();
    setModules([...modules, newMod]);
    setExpandedId(newMod.id);
  };

  const updateModule = (idx, field, value) => {
    const updated = [...modules];
    updated[idx] = { ...updated[idx], [field]: value };
    setModules(updated);
  };

  const removeModule = (idx) => {
    if (!window.confirm('Remove this module?')) return;
    setModules(modules.filter((_, i) => i !== idx));
  };

  const moveModule = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= modules.length) return;
    const updated = [...modules];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setModules(updated);
  };

  const addPdf = (idx) => {
    const updated = [...modules];
    const pdfs = updated[idx].pdf_urls || [];
    updated[idx] = { ...updated[idx], pdf_urls: [...pdfs, { title: '', url: '' }] };
    setModules(updated);
  };

  const updatePdf = (modIdx, pdfIdx, field, value) => {
    const updated = [...modules];
    const pdfs = [...(updated[modIdx].pdf_urls || [])];
    pdfs[pdfIdx] = { ...pdfs[pdfIdx], [field]: value };
    updated[modIdx] = { ...updated[modIdx], pdf_urls: pdfs };
    setModules(updated);
  };

  const removePdf = (modIdx, pdfIdx) => {
    const updated = [...modules];
    const pdfs = (updated[modIdx].pdf_urls || []).filter((_, i) => i !== pdfIdx);
    updated[modIdx] = { ...updated[modIdx], pdf_urls: pdfs };
    setModules(updated);
  };

  const handleSave = async () => {
    const cleaned = modules.map((m) => ({
      ...m,
      duration: m.duration ? parseInt(m.duration) || 0 : 0,
      pdf_urls: (m.pdf_urls || []).filter((p) => p.url),
    }));
    setSaving(true);
    try {
      await api.put(`/admin/courses/${course.course_id}/modules`, { modules: cleaned });
      toast.success('Modules saved');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save modules');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="module-editor">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="module-editor-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h2 className="text-xl text-white font-outfit">{course.title}</h2>
            <p className="text-sm text-zinc-500">{modules.length} module{modules.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={addModule} className="bg-zinc-800 text-white hover:bg-zinc-700" data-testid="add-module-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Module
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="save-modules-btn">
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>

      {modules.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 p-12 text-center">
          <Play className="w-12 h-12 text-zinc-600 mx-auto mb-3" weight="duotone" />
          <p className="text-zinc-400 mb-4">No modules yet. Add your first module to get started.</p>
          <Button onClick={addModule} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
            <Plus className="w-4 h-4 mr-1" /> Add Module
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {modules.map((mod, idx) => {
            const isExpanded = expandedId === mod.id;
            return (
              <div key={mod.id} className={`bg-[#121212] border transition-colors ${isExpanded ? 'border-[#D4AF37]/40' : 'border-white/5'}`} data-testid={`module-item-${idx}`}>
                {/* Module Header */}
                <div
                  className="flex items-center gap-3 p-4 cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : mod.id)}
                >
                  <span className="text-xs font-mono text-zinc-500 w-6">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{mod.title || 'Untitled Module'}</p>
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                      {mod.video_url && <span className="flex items-center gap-1"><Play className="w-3 h-3" /> Video</span>}
                      {mod.text_content && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Text</span>}
                      {(mod.pdf_urls?.length > 0) && <span className="flex items-center gap-1"><FilePdf className="w-3 h-3" /> {mod.pdf_urls.length} PDF{mod.pdf_urls.length > 1 ? 's' : ''}</span>}
                      {mod.duration > 0 && <span>{mod.duration} min</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => moveModule(idx, -1)} disabled={idx === 0} className="text-zinc-500 hover:text-white h-7 w-7 p-0">
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => moveModule(idx, 1)} disabled={idx === modules.length - 1} className="text-zinc-500 hover:text-white h-7 w-7 p-0">
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => removeModule(idx)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0">
                      <Trash className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Editor */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Module Title</Label>
                        <Input value={mod.title} onChange={(e) => updateModule(idx, 'title', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" placeholder="e.g. Introduction to Markets" data-testid={`module-title-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Duration (minutes)</Label>
                        <Input type="number" value={mod.duration} onChange={(e) => updateModule(idx, 'duration', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" placeholder="15" />
                      </div>
                    </div>

                    {/* Video: Upload or URL */}
                    <VideoInput
                      videoUrl={mod.video_url || ''}
                      videoSource={mod.video_source || (mod.video_url?.startsWith('/api/uploads') ? 'upload' : 'url')}
                      onChange={(url, source) => {
                        const updated = [...modules];
                        updated[idx] = { ...updated[idx], video_url: url, video_source: source };
                        setModules(updated);
                      }}
                      moduleIdx={idx}
                    />

                    {/* Text Content */}
                    <div>
                      <Label className="text-zinc-400 flex items-center gap-1"><FileText className="w-3 h-3" /> Text / Notes</Label>
                      <Textarea value={mod.text_content || ''} onChange={(e) => updateModule(idx, 'text_content', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white min-h-[120px]" placeholder="Add lesson notes, key points, or instructions..." data-testid={`module-text-${idx}`} />
                    </div>

                    {/* PDF Links */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-zinc-400 flex items-center gap-1"><FilePdf className="w-3 h-3" /> PDF Resources</Label>
                        <Button variant="ghost" size="sm" onClick={() => addPdf(idx)} className="text-[#D4AF37] hover:text-[#E5C158] h-7 text-xs" data-testid={`add-pdf-${idx}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add PDF
                        </Button>
                      </div>
                      {(mod.pdf_urls || []).map((pdf, pdfIdx) => (
                        <div key={pdfIdx} className="flex gap-2 mb-2">
                          <Input value={pdf.title} onChange={(e) => updatePdf(idx, pdfIdx, 'title', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white flex-1" placeholder="PDF Title" />
                          <Input value={pdf.url} onChange={(e) => updatePdf(idx, pdfIdx, 'url', e.target.value)} className="bg-zinc-900 border-zinc-700 text-white flex-[2]" placeholder="https://... (PDF URL)" />
                          <Button variant="ghost" size="sm" onClick={() => removePdf(idx, pdfIdx)} className="text-red-400 hover:text-red-300 h-9 w-9 p-0">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      {(!mod.pdf_urls || mod.pdf_urls.length === 0) && (
                        <p className="text-xs text-zinc-600">No PDFs added yet.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CoursesTab = ({ courses, onCreateCourse, onDeleteCourse, onRefresh }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', thumbnail_url: '', tier_required: 'basic'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onCreateCourse(courseForm);
    setIsDialogOpen(false);
    setCourseForm({ title: '', description: '', thumbnail_url: '', tier_required: 'basic' });
  };

  if (editingCourse) {
    return (
      <ModuleEditor
        course={editingCourse}
        onBack={() => setEditingCourse(null)}
        onRefresh={() => { onRefresh(); setEditingCourse(null); }}
      />
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-white font-outfit">Courses</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="add-course-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Course
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Course</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-400">Title</Label>
                <Input value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" required />
              </div>
              <div>
                <Label className="text-zinc-400">Description</Label>
                <Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" required />
              </div>
              <div>
                <Label className="text-zinc-400">Thumbnail URL</Label>
                <Input value={courseForm.thumbnail_url} onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })} className="bg-zinc-900 border-zinc-700 text-white" />
              </div>
              <div>
                <Label className="text-zinc-400">Required Tier</Label>
                <Select value={courseForm.tier_required} onValueChange={(value) => setCourseForm({ ...courseForm, tier_required: value })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-zinc-700">
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">Create Course</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((course) => (
          <div key={course.course_id} className="bg-[#121212] border border-white/5 overflow-hidden" data-testid={`admin-course-${course.course_id}`}>
            <img
              src={course.thumbnail_url || 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400'}
              alt={course.title}
              className="w-full h-32 object-cover"
            />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-white font-medium flex-1 truncate">{course.title}</h3>
                <span className={`text-xs px-2 py-1 ${getTierClassName(course.tier_required)}`}>{course.tier_required}</span>
              </div>
              <p className="text-zinc-400 text-sm mb-3 line-clamp-2">{course.description}</p>
              <p className="text-xs text-zinc-500 mb-3">{course.modules?.length || 0} modules</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setEditingCourse(course)}
                  className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700"
                  data-testid={`manage-modules-${course.course_id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Manage Modules
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteCourse(course.course_id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default CoursesTab;
